<script lang="ts" setup>
import { ref, provide, watch } from 'vue'
import { defineProps } from 'vue'
//echarts
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { LineChart, BarChart } from 'echarts/charts'

import {
  TooltipComponent,
  GridComponent,
  DatasetComponent,
  TransformComponent,
  LegendComponent,
} from 'echarts/components'

import VChart, { THEME_KEY } from 'vue-echarts'

interface GrowthData {
  date: string
  growth: number
  newRoots?: number
}

const props = defineProps({
  growthData: {
    type: Array as () => GrowthData[],
    required: true,
  },
})

// echarts
use([
  LineChart,
  BarChart,
  TooltipComponent,
  GridComponent,
  DatasetComponent,
  TransformComponent,
  LegendComponent,
  CanvasRenderer,
])

provide(THEME_KEY, 'light')

const option = ref({
  tooltip: {
    trigger: 'axis',
    axisPointer: {
      type: 'shadow'
    },
    formatter: (params: any) => {
      const data = params[0]
      return `
        <div style="padding: 8px;">
          <strong>${data.axisValue}</strong><br/>
          Crecimiento: ${data.value}%<br/>
          ${data.data.newRoots ? `Raíces nuevas: ${data.data.newRoots}` : 'Sin datos de raíces'}
        </div>
      `
    }
  },
  grid: {
    left: '3%',
    right: '4%',
    bottom: '3%',
    containLabel: true
  },
  xAxis: {
    type: 'category',
    data: [] as string[],
    axisLabel: {
      rotate: 45,
      fontSize: 10
    }
  },
  yAxis: {
    type: 'value',
    name: 'Crecimiento (%)',
    axisLabel: {
      formatter: '{value}%'
    }
  },
  series: [
    {
      name: 'Crecimiento',
      type: 'bar',
      data: [] as any[],
      itemStyle: {
        color: function(params: any) {
          const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD']
          return colors[params.dataIndex % colors.length]
        }
      },
      emphasis: {
        itemStyle: {
          shadowBlur: 10,
          shadowOffsetX: 0,
          shadowColor: 'rgba(0, 0, 0, 0.5)'
        }
      }
    }
  ]
})

const chartRef = ref<InstanceType<typeof VChart> | null>(null)

const updateChartData = (data: GrowthData[]) => {
  const dates = data.map(item => {
    const date = new Date(item.date)
    return date.toLocaleDateString('es-ES', { month: 'short', day: 'numeric' })
  })
  
  const growthValues = data.map((item, index) => ({
    value: item.growth,
    newRoots: item.newRoots,
    itemStyle: {
      color: item.growth > 10 ? '#4CAF50' : item.growth > 5 ? '#FF9800' : '#FF5722'
    }
  }))

  option.value.xAxis.data = dates
  option.value.series[0].data = growthValues
}

watch(
  () => props.growthData,
  (newData) => {
    updateChartData(newData)
  },
  { immediate: true, deep: true }
)
</script>

<template>
  <v-chart ref="chartRef" class="chart" :option="option" style="height: 250px; width: 100%" />
</template>
